import * as React from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';

import PromptExit from 'libs/router/prompt-exit/PromptExit';

import { FormActions } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';

import BrandBusinessDetailsSettingsForm from '../settings/BrandBusinessDetailsSettingsForm';
import InfluencerBusinessDetailsSettingsForm from '../settings/influencer/InfluencerBusinessDetailsSettingsForm';
import BrandAddressSettingsForm from '../settings/BrandAddressSettingsForm';
import BankDetailsSettingsForm from '../settings/BankDetailsSettingsForm';

import { saveOrganizationSettings } from '../../reducers/organizationSettings';
import { fetchCountryCodes } from 'modules/locations/reducers/worldLocation';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { danger } from 'redux-store/reducers/common-reducer';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { API } from 'utils/api';
import { __t, applyTranslations } from 'utils/language';
import { uploadFile } from 'utils/upload-asset';
import {
  isBrandsFocusedOrganization,
  prepareSettingsPayload
} from 'utils/organization';
import { addProtocol } from 'utils/url';
import { getPhone } from 'utils/helper/common';

applyTranslations(require('./organization-settings.i18n.json'));

import type {
  BankDetails,
  BillingAddress,
  BusinessDetails,
  LocationSuggestionCityOrCountry,
  MixedSelectDropdownItem,
  Organization,
  OrganizationSettingsForm,
  OrganizationSettings as OrganizationSettingsType,
  ProofDocumentFile,
  ProofDocuments,
  TextEntryKeyboardEvent,
  UploadFileResponse
} from 'types';

type OwnProps = {
  currentOrganization: Organization,
  organizationSettings: OrganizationSettingsType,
  organizationType: string | undefined,
  settingsGroup: 'brandBusinessDetails' | 'influencerBusinessDetails' | 'bankDetails' | 'billingAddress',
  isDisabled: boolean,
  onModeSwitch: (activeForm?: string | null) => void,
  onLocationInput?: (country: string) => void
};

type DispatchProps = {
  dispatchSaveOrganizationSettings: (
    settingsId: number,
    trimmedPayload: OrganizationSettingsForm
  ) => Promise<void>,
  dispatchShowLoading: (url: string) => void,
  dispatchHideLoading: (url: string) => void,
  dispatchShowError: (errorMessage: string) => void
};

type Props = OwnProps & DispatchProps;

type State = {
  organizationDetails: {
    bankDetails: BankDetails,
    billingAddress: BillingAddress,
    businessDetails: BusinessDetails,
    proofDocuments: ProofDocuments | null
  },
  fileToUpload: {
    file: ProofDocumentFile,
    name: string,
    type: string
  } | null,
  canEdit: boolean,
  isModified: boolean,
  countries: Array<MixedSelectDropdownItem>
};

const msgKey = 'organization_settings';

export class OrganizationSettings extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      organizationDetails: this.getOrganizationDetailsState(props),
      fileToUpload: null,
      canEdit: false,
      isModified: false,
      countries: []
    };
  }

  componentDidMount() {
    const { organizationDetails: { billingAddress } } = this.state;

    fetchCountryCodes(billingAddress.phone.country).then(countries => {
      this.setState(prevState => ({ ...prevState, countries }));
    });
  }

  getParsedPhoneObject = (): BillingAddress['phone'] => {
    const phone = this.props.organizationSettings?.billingAddress?.phone;

    return getPhone(phone?.originalNumber || phone?.number);
  };

  getOrganizationDetailsState(nextProps: Props): State['organizationDetails'] {
    const { organizationSettings } = nextProps;
    const { billingAddress, businessDetails, bankDetails, proofDocuments } = organizationSettings;
    const entrepreneur = this.handleBooleanValue(businessDetails?.entrepreneur);
    const phone = this.getParsedPhoneObject();

    return {
      bankDetails: {
        accountNumber: bankDetails?.accountNumber ?? '',
        swift: bankDetails?.swift ?? '',
        accountHolder: bankDetails?.accountHolder ?? ''
      },
      billingAddress: {
        legalName: billingAddress?.legalName ?? '',
        addressLine1: billingAddress?.addressLine1 ?? '',
        city: billingAddress?.city ?? '',
        state: billingAddress?.state ?? '',
        postalCode: billingAddress?.postalCode ?? '',
        country: billingAddress?.country ?? '',
        phone
      },
      businessDetails: {
        companyName: businessDetails?.companyName ?? '',
        registeredBusiness: true,
        taxId: businessDetails?.taxId ?? '',
        website: businessDetails?.website ?? '',
        taxIdCategory: businessDetails?.taxIdCategory || null,
        entrepreneur
      },
      proofDocuments
    };
  }

  getTrimmedPayload = (): OrganizationSettingsForm => {
    const { organizationDetails } = this.state;

    const payload = {
      ...organizationDetails,
      billingAddress: {
        ...organizationDetails.billingAddress,
        phone: {
          countryCode: '',
          country: undefined,
          number: organizationDetails.billingAddress?.phone?.originalNumber || '',
          originalNumber: ''
        }
      }
    };


    const trimmedPayload = prepareSettingsPayload(payload);

    // add protocol, if needed
    trimmedPayload.businessDetails.website = addProtocol(trimmedPayload.businessDetails.website);

    return trimmedPayload;
  };

  handleBooleanValue(value: BusinessDetails['entrepreneur']): boolean | string {
    return value || (value === false ? false : '');
  }

  enterEditMode = () => {
    const { settingsGroup, onModeSwitch } = this.props;

    this.setState({
      canEdit: true,
      isModified: false
    });

    onModeSwitch(settingsGroup);
  };

  leaveEditMode = () => {
    const { onModeSwitch } = this.props;

    this.setState({
      canEdit: false,
      isModified: false,
      organizationDetails: this.getOrganizationDetailsState(this.props)
    });

    onModeSwitch(null);
  };

  handleSave = async () => {
    const { currentOrganization, onModeSwitch, dispatchSaveOrganizationSettings } = this.props;
    const { fileToUpload } = this.state;
    const settingsId = currentOrganization.agencyOrganizationId || currentOrganization.id;
    const includesFileChange = !isNil(fileToUpload);
    let fileHandlerResult: boolean | null = null;

    // upload file to S3
    if (!isNil(fileToUpload)) {
      fileHandlerResult = await this.handleUpload();
    }

    if (!includesFileChange || fileHandlerResult) {
      const trimmedPayload = this.getTrimmedPayload();

      dispatchSaveOrganizationSettings(settingsId, trimmedPayload)
        .then(() => {
          addUserAction({
            eventCategory: 'business-bank-details',
            eventAction: 'business-bank-details-updated',
            eventLabel: 'desktop | profile'
          });

          onModeSwitch(null);
        })
        .catch(() => { });
    }
  };

  handleTextInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string | undefined = undefined,
    uppercase?: boolean
  ) => {
    const input = event.target;
    const value = uppercase ? (input.value || '').toUpperCase() : input.value;

    this.handleChange(value, input.name, type);
  };

  handleChange = (
    value: UploadFileResponse | ProofDocumentFile | string | null,
    name: string,
    type?: string
  ) => {
    if (!type) {
      this.setState(state => ({
        organizationDetails: {
          ...state.organizationDetails,
          [name]: value
        },
        isModified: true
      }));
    } else {
      this.setState(state => ({
        organizationDetails: {
          ...state.organizationDetails,
          [type]: {
            ...state.organizationDetails[type],
            [name]: value
          }
        },
        isModified: true
      }));
    }
  };

  handlePreUpload = (
    value: ProofDocumentFile,
    name: string,
    type: string
  ): void => {
    this.handleChange(value, name, type);
    this.setState({
      fileToUpload: {
        file: value,
        name,
        type
      }
    });
  };

  handleUpload = async (): Promise<boolean> => {
    const { dispatchShowLoading, dispatchHideLoading, dispatchShowError } = this.props;
    const { fileToUpload } = this.state;
    const url = API.END_POINTS.API.UPLOAD_DOCUMENT;

    dispatchShowLoading(url);

    if (!isNil(fileToUpload)) {
      const response = await uploadFile(fileToUpload.file.data, url);

      if (response.type === 'success') {
        this.handleChange(response.data, fileToUpload.name, fileToUpload.type);

        this.setState({
          fileToUpload: null
        });

        dispatchHideLoading(url);
        return true;
      }
    }

    dispatchHideLoading(url);
    dispatchShowError(__t(`${msgKey}.upload_error`));

    return false;
  };

  handlePhoneNumberEdit = (
    value: string,
    field: string,
    countries?: Array<MixedSelectDropdownItem>
  ): void => {
    const parsedPhone = this.getParsedPhoneObject();
    const originalNumber = field === 'countryCode' ?
      `${value} ${parsedPhone?.number}` : `${parsedPhone?.countryCode} ${value}`;

    this.setState(prevState => ({
      ...prevState,
      ...(countries ? { countries: [...countries] } : {}),
      organizationDetails: {
        ...prevState.organizationDetails,
        billingAddress: {
          ...prevState.organizationDetails.billingAddress,
          phone: {
            ...parsedPhone,
            [field]: value,
            originalNumber
          }
        }
      },
      isModified: true
    }));
  };

  handleLocationInput = (
    event: TextEntryKeyboardEvent,
    location: LocationSuggestionCityOrCountry,
    type?: string
  ): void => {
    const { organizationDetails } = this.state;
    const { onLocationInput } = this.props;

    const details = type ? organizationDetails[type] : organizationDetails;

    const updatedLocation = {
      city: location.city || details.city || '',
      state: location.state || details.state || '',
      country: location.country || details.country || ''
    };

    if (!type) {
      this.setState({
        organizationDetails: {
          ...organizationDetails,
          ...updatedLocation
        },
        isModified: true
      });
    } else {
      this.setState({
        organizationDetails: {
          ...organizationDetails,
          [type]: {
            ...organizationDetails[type],
            ...updatedLocation
          }
        },
        isModified: true
      });
    }

    if (onLocationInput) {
      onLocationInput(location.country);
    }
  };

  renderActions(editButtonLabel: string) {
    const { isDisabled } = this.props;

    const editModeButtons: Array<React.ReactElement> = [
      <ActionButton
        key="cancel"
        color="tertiary"
        onClick={() => this.leaveEditMode()}
      >
        {__t(`${msgKey}.button.cancel`)}
      </ActionButton>,
      <ActionButton
        key="save"
        type="submit"
      >
        {__t(`${msgKey}.button.save`)}
      </ActionButton>
    ];

    const viewModeButtons = [
      <ActionButton
        key="edit"
        disabled={isDisabled}
        onClick={this.enterEditMode}
      >
        {editButtonLabel}
      </ActionButton>
    ];

    return (
      <FormActions>
        {
          this.state.canEdit ? editModeButtons : viewModeButtons
        }
      </FormActions>
    );
  }

  render() {
    const { currentOrganization, settingsGroup, organizationType, organizationSettings } = this.props;
    const { isModified, countries } = this.state;
    const isBrand = isBrandsFocusedOrganization(currentOrganization);

    const { organizationDetails } = this.state;
    const { businessDetails, bankDetails, proofDocuments, billingAddress } = organizationDetails;

    if (!currentOrganization || !currentOrganization.id) {
      return null;
    }

    const settingsMap = {
      brandBusinessDetails: {
        buttonLabel: __t(`${msgKey}.button.edit_details`),
        Form: BrandBusinessDetailsSettingsForm,
        values: businessDetails,
        data: 'brand-business-details'
      },
      influencerBusinessDetails: {
        buttonLabel: __t(`${msgKey}.button.edit_details`),
        Form: InfluencerBusinessDetailsSettingsForm,
        values: {
          isFromSettings: true,
          businessDetails,
          billingAddress,
          businessDetailsProof: proofDocuments?.businessDetailsProof
        },
        data: 'influencer-business-details'
      },
      bankDetails: {
        buttonLabel: __t(`${msgKey}.button.edit_bank_details`),
        Form: BankDetailsSettingsForm,
        values: {
          bankDetails,
          bankDetailsProof: proofDocuments?.bankDetailsProof
        },
        data: 'bank-details'
      },
      billingAddress: {
        buttonLabel: __t(isBrand ?
          `${msgKey}.button.edit_address` :
          `${msgKey}.button.edit_business_details`
        ),
        Form: BrandAddressSettingsForm,
        values: billingAddress,
        data: 'billing-address'
      }
    };

    const settings = settingsMap[settingsGroup];

    return (
      <settings.Form
        onSubmit={this.handleSave}
        onTextInput={this.handleTextInput}
        onChange={this.handleChange}
        onPreUpload={this.handlePreUpload}
        onLocationInput={this.handleLocationInput}
        currentOrganization={currentOrganization}
        organizationSettings={organizationSettings}
        canEdit={this.state.canEdit}
        values={settings.values}
        organizationType={organizationType}
        countries={countries}
        handlePhoneNumberEdit={this.handlePhoneNumberEdit}
      >
        {
          isModified &&
            <PromptExit type="page" />
        }
        {this.renderActions(settings.buttonLabel)}
      </settings.Form>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSaveOrganizationSettings: (settingsId: number, trimmedPayload: OrganizationSettingsForm) => (
    dispatch(saveOrganizationSettings(settingsId, trimmedPayload))
  ),
  dispatchShowLoading: (url: string) => dispatch(showLoading(url)),
  dispatchHideLoading: (url: string) => dispatch(hideLoading(url)),
  dispatchShowError: (errorMessage: string) => dispatch(danger(errorMessage))
});

export default connect(null, mapDispatchToProps)(OrganizationSettings);
